import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Lead, TimeZone, User, UserRedirect, WorkingHours } from '../models/user.model';
import { FirestoreService } from './firestore.service';
import { SnackService } from 'src/app/services/snack.service';
import { Roles } from '../models/patient.model';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PhoneConfig } from '../shared/models/phone-config.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  collectionFSName = 'users';
  lastNameFSName = 'lastName';
  rolesFSQuery = 'roles.';
  welbyCoreApiBaseUrl = environment.welbyEndpoint;
  constructor(
    private fs: FirestoreService,
    private firestore: AngularFirestore,
    private snackService: SnackService,
    private http: HttpClient
  ) {}

  updateUser(user: User): Promise<void> {
    return this.fs.update(`users/${user.user_id}`, user);
  }

  updateTimezone(userId: string, timezone: TimeZone): Promise<void> {
    return this.firestore.collection('users').doc(userId).update({
      timezone,
    });
  }

  updateUserAiMessage(userId: string, aiMessageValue: boolean, signature: string) {
    return this.firestore.collection('users').doc(userId).update({
      enableAImessage: aiMessageValue,
      smsSignature: signature,
    });
  }

  updatePhone(userId: string, phone: string): Promise<void> {
    return this.firestore.collection('users').doc(userId).update({ phone });
  }

  updateMyPatientPanel(userId: string, itemPerPage: number): Promise<void> {
    return this.firestore
      .collection('users')
      .doc(userId)
      .update({ 'config.defaultItemPerPage': itemPerPage });
  }

  updateTasksExpandedPreference(userId: string, value: boolean): Promise<void> {
    return this.firestore
      .collection('users')
      .doc(userId)
      .update({ 'config.defaultTasksExpanded': value });
  }

  async updateOnCallStatus(onCallUser: any): Promise<void> {
    this.firestore.collection('users').doc(onCallUser.userId).update({ isOnCall: onCallUser.isOnCall });

    const userOnCall = await this.fs
      .col('on_call_nurses')
      .ref.where('userId', '==', onCallUser.userId)
      .where('isOnCall', '==', true)
      .get();

    if (userOnCall.size > 0 && !onCallUser.isOnCall) {
      this.firestore
        .collection('on_call_nurses')
        .doc(userOnCall.docs[0].id)
        .update({ isOnCall: onCallUser.isOnCall, endDate: onCallUser.endDate });
    } else {
      this.firestore.collection('on_call_nurses').add(onCallUser);
    }
    return Promise.resolve();
  }

  updateLanguages(userId: string, languages: string[]): Promise<void> {
    return this.firestore.collection('users').doc(userId).update({ languages });
  }

  getUserById(id: string) {
    return this.fs.doc(`users/${id}`).get();
  }

  getUsers(pageSize: number, paginationParams?: any, searchParams?: any) {
    return this.firestore
      .collection(this.collectionFSName, (ref) => {
        let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
        query = searchParams ? this.getQueryFromSearchParams(query, searchParams) : query;
        if (!searchParams?.lastName && !(searchParams?.clientAccounts && this.hasExactMatch(searchParams))) {
          query = query.orderBy(this.lastNameFSName);
        }
        query = paginationParams ? this.getQueryFromPaginatorParams(query, paginationParams) : query;
        query = query.limit(pageSize);
        return query;
      })
      .get()
      .pipe(
        map((results) => results.docs.map((result) => ({ ...(result.data() as any), doc: result }))),
        map((users) =>
          searchParams?.clientAccounts ? users.sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? '')) : users
        )
      );
  }

  updateRoles(userId: string, updatedRoles: Roles): void {
    const userDoc = this.fs.col('users').doc(userId);
    userDoc.update({ roles: updatedRoles }).catch((error) => {
      console.error('Error updating roles in Firebase:', error);
      this.snackService.genericSnackBar('Role successfully deleted', ['success-snackbar'], 5000);
    });
  }

  /**
   * Atomically persist a user's roles and, when a clinical-role toggle changes conference-call
   * eligibility, the `enable_conference_call` flag in the same write — so the two never drift apart.
   * `conferenceCall` is left `undefined` for non-clinical role changes so the flag is untouched.
   * Returns the update promise so callers can surface real success/failure instead of assuming success.
   */
  updateRolesAndConferenceCall(userId: string, roles: Roles, conferenceCall?: boolean): Promise<void> {
    const payload: { roles: Roles; enable_conference_call?: boolean } = { roles };
    if (conferenceCall !== undefined) {
      payload.enable_conference_call = conferenceCall;
    }
    return this.firestore.collection('users').doc(userId).update(payload);
  }

  getLeadNurses(): Observable<any> {
    return this.fs.colWithIds$('users', (ref) =>
      ref.where('roles.isManagerNurse', '==', true).orderBy('firstName', 'asc')
    );
  }

  getTeamByLeadId(leadId: string): Observable<User[]> {
    return this.fs.colWithIds$<User>('users', (ref) => ref.where('lead.id', '==', leadId));
  }

  getProvidersRoles(): Observable<string[]> {
    return this.fs
      .doc<{ roles: string[] }>('welby_configuration/provider_roles')
      .get()
      .pipe(map((doc) => (doc.exists ? doc.data()?.roles || [] : [])));
  }

  updateLeadNurse(providerId: string, lead: Lead): Promise<void> {
    return this.fs.update(`users/${providerId}`, { lead });
  }

  /**
   * Get client phones for a specific user
   *
   * @param userId The user ID to get client phones for
   * @returns Observable with the client phones data
   */
  getClientPhones(userId: string): Observable<PhoneConfig[]> {
    const url = `${this.welbyCoreApiBaseUrl}/api/v1/users/${userId}/client-phones`;
    return this.http.get<PhoneConfig[]>(url);
  }

  getWorkingHours(userId: string): Observable<WorkingHours> {
    return this.http.get<WorkingHours>(`${this.welbyCoreApiBaseUrl}/api/v1/users/${userId}/working-hours`);
  }

  updateWorkingHours(userId: string, workingHours: WorkingHours): Observable<{ message: string; status: string }> {
    return this.http.put<{ message: string; status: string }>(
      `${this.welbyCoreApiBaseUrl}/api/v1/users/${userId}/working-hours`,
      workingHours
    );
  }

  getRedirect(userId: string): Observable<UserRedirect | null> {
    return this.http.get<UserRedirect | null>(`${this.welbyCoreApiBaseUrl}/api/v1/users/${userId}/redirect`);
  }

  updateRedirect(userId: string, redirectUserId: string): Observable<{ message: string; status: string }> {
    return this.http.put<{ message: string; status: string }>(
      `${this.welbyCoreApiBaseUrl}/api/v1/users/${userId}/redirect`,
      { redirectUserId }
    );
  }

  getWelbyClinicalStaff(): Observable<User[]> {
    return this.http.get<User[]>(`${this.welbyCoreApiBaseUrl}/api/v1/users/welby-clinical-staff`);
  }

  private getQueryFromSearchParams(
    ref: firebase.firestore.CollectionReference | firebase.firestore.Query,
    searchParams: any
  ): firebase.firestore.CollectionReference | firebase.firestore.Query {
    let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
    if (searchParams.role) {
      query = query.where(`${this.rolesFSQuery}${searchParams.role}`, '==', true);
    }
    if (searchParams.clientAccounts) {
      query = query.where('client_responsible_id', 'in', searchParams.clientAccounts);
    }
    if (searchParams.firstName) {
      query = query.where(`firstName`, '==', searchParams.firstName);
    }
    if (searchParams.lastName) {
      query = query.where(`lastName`, '==', searchParams.lastName);
    }
    if (searchParams.email) {
      query = query.where(`email`, '==', searchParams.email);
    }
    if (searchParams.user_id) {
      query = query.where(`user_id`, '==', searchParams.user_id);
    }
    if (searchParams.searchTerms) {
      query = query.where('search_terms', 'array-contains', searchParams.searchTerms[0]);
    }
    return query;
  }

  private getQueryFromPaginatorParams(
    ref: firebase.firestore.CollectionReference | firebase.firestore.Query,
    paginatorParams: any
  ): firebase.firestore.CollectionReference | firebase.firestore.Query {
    let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
    if (paginatorParams && paginatorParams.lastResolution && paginatorParams.isNextClicked) {
      query = query.startAfter(paginatorParams.lastResolution);
    }
    if (paginatorParams && paginatorParams.firstResolution && paginatorParams.isPrevClicked) {
      query = query.startAt(paginatorParams.firstResolution);
    }
    if (paginatorParams && paginatorParams.lastResolution && paginatorParams.isPrevClicked) {
      query = query.endAt(paginatorParams.lastResolution);
    }

    return query;
  }

  // Clinic-scoped exact searches skip orderBy so they stay equality-only and need no new composite index.
  private hasExactMatch(searchParams: any): boolean {
    return !!(searchParams.firstName || searchParams.email || searchParams.user_id);
  }
}
